import React, { useContext } from "react";
import { ThumbnailSelectionMode } from "./types";
import millisecondsTohms from "../../utils/millisecondsTohms";
import { Photo } from "./types";
import { ImageWithLoader } from "./ImageWithLoader";
const hiddenIcon = require("./styles/imgHide.svg");
const playIcon = require("./styles/play.svg");

import {
  CartOutline,
  CartFilled
} from "../BrowseEvent/components/vectors/AddToCartIcon";
import {
  CheckOutline,
  CheckFilled
} from "../BrowseEvent/components/vectors/CheckIcon";
import { EventGalleryContext } from "./index";
import { OrgDownloadContext } from "../OrganizationPhotos/OrgDownloadContext";

export const ThumbnailHeight = 120;
export const ThumbnailWidth = 120;
const ThumbnailMargin = 10;

const PaidMediaIcon = () => (
  <div
    className="geo-cart-icon-container paid-media-symbol-container"
    aria-label="Paid media – add to cart"
    title="Paid media"
  >
    <span aria-hidden="true">$</span>
  </div>
);

export const ImageSelectIcon = ({
  photo,
  index,
  onThumbnailSelect,
  primaryColor,
  selectionMode,
  securedOrgView,
  disableSelectIcon,
  directDownloadEnabled,
  shouldShowDownloadSelect,
  handleDownloadImageSelection
}: {
  photo: Photo;
  index?: number;
  onThumbnailSelect: (photo: Photo, index?: number) => void;
  primaryColor?: string;
  selectionMode: ThumbnailSelectionMode;
  securedOrgView?: boolean;
  disableSelectIcon?: boolean;
  directDownloadEnabled?: boolean;
  shouldShowDownloadSelect?: () => boolean;
  handleDownloadImageSelection?: (data: {
    photo: Photo;
    index?: number;
  }) => void;
}) => {
  const context = useContext(EventGalleryContext);
  const orgDownloadContext = useContext(OrgDownloadContext);

  const defaultColor =
    primaryColor || context?.pageConfiguration?.primaryColor || "#34b2a7";

  const showPaidMediaIcon =
    directDownloadEnabled &&
    orgDownloadContext?.fromSecuredFaceSearch &&
    photo.album?.event?.price !== 0;

  if (showPaidMediaIcon) {
    return <PaidMediaIcon />;
  }

  if (disableSelectIcon) {
    return null;
  }

  if (context?.endUserDownloadsDisabled || orgDownloadContext?.endUserDownloadsDisabled) {
    return null;
  }

  // Determine if we should show a check icon (download mode) or cart icon (purchase mode)
  const shouldUseCheckIcon = () => {
    return (
      securedOrgView ||
      (context?.event?.price === 0 && directDownloadEnabled) ||
      context?.curationMode
    );
  };

  const isPhotoSelected = () => {
    if (orgDownloadContext) {
      return (
        orgDownloadContext.downloadableContent?.includes(photo.id) ?? false
      );
    }
    return photo.isSelected;
  };

  // Check if photo has any products (digital or print) in cart
  const hasProductsInCart = () => {
    // Check if photo is in cart (has digital or print products)
    return photo.isSelected;
  };

  // Tooltip handlers following codebase pattern
  const handleHideTooltip = () => {
    //@ts-ignore
    $(`#cart-tooltip-mobile-${photo.id} [data-toggle="tooltip"]`).tooltip(
      "hide"
    );
  };

  const handleTooltip = () => {
    if (isPhotoSelected() && !shouldUseCheckIcon() && context?.printProductsflow?.()) {
      //@ts-ignore
      $(`#cart-tooltip-mobile-${photo.id} [data-toggle="tooltip"]`)
        .attr("data-original-title", "Products of this photo are in cart")
        .tooltip("show");
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (shouldShowDownloadSelect?.()) {
      handleDownloadImageSelection?.({ photo, index });
    } else if (shouldUseCheckIcon()) {
      onThumbnailSelect(photo);
    } else if (!context?.printProductsflow?.()) {
      onThumbnailSelect(photo);
    }
  };

  const renderIconContent = () => {
    const isSelected = isPhotoSelected();
    if (shouldUseCheckIcon()) {
      return isSelected ? (
        <CheckFilled color={defaultColor} />
      ) : (
        <CheckOutline />
      );
    } else if (isSelected) {
      return (
        <CartFilled
          color={context?.printProductsflow?.() ? "#0000008f" : defaultColor}
        />
      );
    } else if (!context?.printProductsflow?.()) {
      return <CartOutline />;
    } else {
      return null;
    }
  };

  if (
    !shouldUseCheckIcon() &&
    context?.printProductsflow?.() &&
    !isPhotoSelected()
  ) {
    return null;
  }

  return (
    <div
      id={`cart-tooltip-mobile-${photo.id}`}
      className="geo-cart-icon-container"
      data-toggle="tooltip"
      data-placement="top"
      onClick={handleClick}
      onMouseEnter={() => handleTooltip()}
      onMouseLeave={() => handleHideTooltip()}
      style={{
        cursor:
          isPhotoSelected() || context?.printProductsflow?.()
            ? "default"
            : "pointer"
      }}
    >
      {renderIconContent()}
    </div>
  );
};

export const VideoThumbnail = (props: {
  video: Photo & { videoDuration: number };
  index?: number;
  onClick: (photo: Photo) => void;
  onThumbnailSelect: (photo: Photo, index?: number) => void;
  selectionMode: ThumbnailSelectionMode;
  primaryColor?: string;
  securedOrgView?: boolean;
  disableSelectIcon?: boolean;
  directDownloadEnabled?: boolean;
  shouldShowDownloadSelect?: () => boolean;
  handleDownloadImageSelection?: (data: {
    photo: Photo;
    index?: number;
  }) => void;
}) => {
  const {
    video,
    index,
    onClick,
    onThumbnailSelect,
    primaryColor,
    securedOrgView,
    disableSelectIcon,
    directDownloadEnabled,
    shouldShowDownloadSelect,
    handleDownloadImageSelection
  } = props;

  const isHidden = video.hidden || false;

  const handleClick = (evt: React.MouseEvent<HTMLImageElement>) => {
    evt.stopPropagation();
    onClick(video);
  };

  return (
    <div
      className="thumbnail-box"
      style={{ textAlign: "center", cursor: "pointer" }}
    >
      {isHidden && (
        <span className="visibility-stamp">
          <img src={hiddenIcon} alt="" />
        </span>
      )}

      <ImageSelectIcon
        photo={video}
        index={index}
        onThumbnailSelect={onThumbnailSelect}
        primaryColor={primaryColor}
        selectionMode={props.selectionMode}
        securedOrgView={securedOrgView}
        disableSelectIcon={disableSelectIcon}
        directDownloadEnabled={directDownloadEnabled}
        shouldShowDownloadSelect={shouldShowDownloadSelect}
        handleDownloadImageSelection={handleDownloadImageSelection}
      />

      <span className="time-stamp">
        {millisecondsTohms(video.videoDuration)}
      </span>

      <img
        className="play-icon"
        src={playIcon}
        alt="play-icon"
        onClick={handleClick}
      />

      <ImageWithLoader
        src={video.thumbnail}
        alt={`${video.album.name}-${video.id}`}
        onClick={handleClick}
        height={ThumbnailHeight}
        placeholderHeight={ThumbnailHeight - ThumbnailMargin}
        placeholderWidth={ThumbnailWidth - ThumbnailMargin}
      />
    </div>
  );
};

export const ImageThumbnail = (props: {
  photo: Photo;
  index?: number;
  onClick: (photo: Photo) => void;
  onThumbnailSelect: (photo: Photo, index?: number) => void;
  selectionMode: ThumbnailSelectionMode;
  primaryColor?: string;
  securedOrgView?: boolean;
  disableSelectIcon?: boolean;
  directDownloadEnabled?: boolean;
  shouldShowDownloadSelect?: () => boolean;
  handleDownloadImageSelection?: (data: {
    photo: Photo;
    index?: number;
  }) => void;
}) => {
  const {
    photo,
    index,
    onClick,
    onThumbnailSelect,
    primaryColor,
    securedOrgView,
    disableSelectIcon,
    directDownloadEnabled,
    shouldShowDownloadSelect,
    handleDownloadImageSelection
  } = props;

  // Check if the photo is hidden
  const isHidden = photo.hidden || false;

  const handleClick = (evt: React.MouseEvent<HTMLImageElement>) => {
    evt.stopPropagation();
    onClick(photo);
  };

  const imgAlt = `${photo.album.name}-${photo.id}`;

  return (
    <div
      className="thumbnail-box"
      style={{ objectFit: "contain", margin: "5px auto" }}
    >
      {isHidden && (
        <span className="visibility-stamp">
          <img src={hiddenIcon} alt="" />
        </span>
      )}
      <ImageSelectIcon
        photo={photo}
        index={index}
        onThumbnailSelect={onThumbnailSelect}
        primaryColor={primaryColor}
        selectionMode={props.selectionMode}
        securedOrgView={securedOrgView}
        disableSelectIcon={disableSelectIcon}
        directDownloadEnabled={directDownloadEnabled}
        shouldShowDownloadSelect={shouldShowDownloadSelect}
        handleDownloadImageSelection={handleDownloadImageSelection}
      />

      <ImageWithLoader
        src={photo.thumbnail}
        alt={imgAlt}
        className="image-thumbnail"
        height={ThumbnailHeight - ThumbnailMargin}
        placeholderHeight={ThumbnailHeight - ThumbnailMargin}
        placeholderWidth={ThumbnailWidth - ThumbnailMargin}
        onClick={handleClick}
      />
    </div>
  );
};

const ImageThumbnailMemo = React.memo(ImageThumbnail);

export const EnhancedThumbnail = (props: {
  photo: Photo;
  index?: number;
  onClick: (photo: Photo) => void;
  onThumbnailSelect: (photo: Photo, index?: number) => void;
  selectionMode: ThumbnailSelectionMode;
  primaryColor?: string;
  securedOrgView?: boolean;
  disableSelectIcon?: boolean;
  directDownloadEnabled?: boolean;
  shouldShowDownloadSelect?: () => boolean;
  handleDownloadImageSelection?: (data: {
    photo: Photo;
    index?: number;
  }) => void;
}) => {
  return props.photo.videoContent ? (
    <VideoThumbnail {...props} video={props.photo} />
  ) : (
    <ImageThumbnailMemo {...props} />
  );
};
